import { HelpDocumentEdit } from '../model/help-document-edit';
import { UntypedFormBuilder, Validators, UntypedFormControl } from '@angular/forms';
import { Component, Input, Output, EventEmitter } from '@angular/core';

@Component({
  selector: 'app-help-document-edit',
  templateUrl: './help-document-edit.component.html',
  styleUrls: ['./help-document-edit.component.scss']
})
export class HelpDocumentEditComponent
{
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public save: EventEmitter<HelpDocumentEdit> = new EventEmitter();

  public editForm: any;
  public editFormActive = false;
  public isLocked = false;
  public importFileSelected = false;

  public urlPattern = /^(?:https?:\/\/)?[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/;

  private helpDocumentEdit: HelpDocumentEdit;
  private selectedFileMimeType: string;
  private selectedFile: any = null;

  constructor(
    private fb: UntypedFormBuilder)
  { }

  public get name(): UntypedFormControl
  {
    return this.editForm.get('name');
  }

  public get importFilename(): UntypedFormControl
  {
    return this.editForm.get('importFilename');
  }

  public get type(): UntypedFormControl
  {
    return this.editForm.get('type');
  }

  public get url(): UntypedFormControl
  {
    return this.editForm.get('url');
  }

  @Input() public set model(helpDocumentObj: HelpDocumentEdit)
  {
    this.initModel(helpDocumentObj);
  }

  public onSave(e: Event): void
  {
    e.preventDefault();

    const objectToPersist = this.editForm.value;

    // Set Document object
    if (this.importFileSelected && this.type.value === 'DOCUMENT')
    {
      if (objectToPersist.document === null || objectToPersist.document === undefined)
      {
        objectToPersist.document = {};
      }
      objectToPersist.document.name = this.importFilename.value;
      objectToPersist.document.mimeType = this.selectedFileMimeType;
      const base64 = this.selectedFile.split('base64,')[1];
      objectToPersist.document.file = base64;
    }
    else
    {
      objectToPersist.document = null;
    }

    delete objectToPersist.importFilename;

    objectToPersist.type = this.type.value;

    this.save.emit(objectToPersist);
    this.editFormActive = false;
  }

  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Process the file import.
   */
  public handlePdfInput(files: FileList): void
  {
    const importedFile = files.item(0);

    this.importFileSelected = true;
    this.editForm.importFilename = importedFile.name;
    this.importFilename.setValue(importedFile.name);
    this.selectedFileMimeType = importedFile.type;
    this.editForm.markAsDirty();
    this.setFileFromBlob(importedFile);
  }

  public isValidUri(uri: UntypedFormControl): boolean
  {
    const regEx = new RegExp(this.urlPattern);
    return uri.value !== undefined && uri.value && regEx.test(uri.value);
  }

  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }

  private initModel(helpDocument: HelpDocumentEdit)
  {
    if (helpDocument === undefined)
    {
      return;
    }

    const urlValidatorsRequired = [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ];

    // Set form
    this.editForm = this.fb.group({
      id: [this.isNew ? null : helpDocument.id],
      name: [
        {
          value: this.isNew ? null : helpDocument.name,
          disabled: false
        },
        {
          validators: [Validators.required]
        }
      ],
      importFilename: [
        {
          value: this.isNew || !helpDocument.document ? null : helpDocument.document.name,
          disabled: true
        },
        {
          validators: [Validators.required]
        }
      ],
      url: [
        {
          value: this.isNew ? null : helpDocument.url,
          disabled: false
        },
        {
          validators: urlValidatorsRequired
        }
      ],
      type: [
        {
          value: helpDocument.type !== '' ? helpDocument.type : 'DOCUMENT',
          disabled: !this.isNew
        },
        {
          validators: [Validators.required]
        }
      ],
      enabled: { value: this.isNew ? true : helpDocument.enabled, disabled: false }
    });

    this.helpDocumentEdit = helpDocument;
    this.editFormActive = true;
  }

  /**
   * Set the selected file.
   *
   * @param file Selected file.
   */
  private setFileFromBlob(file: Blob): void
  {
    const reader = new FileReader();
    reader.addEventListener('load', () =>
    {
      this.selectedFile = reader.result;
    }, false);
    reader.readAsDataURL(file);
  }
}
